import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { queryKeys } from "@/lib/queryKeys";
import {
  type EnforcementAction,
  safetyProvider,
} from "@/providers/safety.provider";
import { UserPicker } from "@/components/user-picker";
import type { User } from "@/providers/users.provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export const ENFORCEMENT_ACTIONS: {
  value: EnforcementAction;
  label: string;
  hint: string;
}[] = [
  {
    value: "issue_warning",
    label: "Warning",
    hint: "Access is unaffected. Sends an email and push notification only.",
  },
  {
    value: "strike_user",
    label: "Strike",
    hint: "A mark on the record for admins — access is unaffected, same as a warning. Sends an email and push notification only.",
  },
  {
    value: "ban_user",
    label: "Ban",
    hint: "Locks the account. Leave the expiry empty for a permanent ban.",
  },
];

export function EnforcementDialog({
  open,
  onOpenChange,
  userId,
  username,
  reportId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit to let the admin search for a user inside the dialog. */
  userId?: string;
  username?: string;
  reportId?: string;
}) {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<EnforcementAction>("strike_user");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [picked, setPicked] = useState<User | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Only a ban locks the account — a strike is just a mark on the record.
  const locks = action === "ban_user";
  const targetId = userId ?? picked?.user_id ?? "";
  const targetName = username ?? picked?.username;

  function reset() {
    setAction("strike_user");
    setReason("");
    setExpiresAt("");
    setPicked(null);
    setConfirming(false);
  }

  const markReviewed = useMutation({
    mutationFn: () =>
      safetyProvider.updateReport(reportId as string, { status: "reviewed" }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "safety-reports-detailed",
      });
    },
    onError: () =>
      toast.warning(
        "Enforcement issued, but couldn't mark the report reviewed — do it manually."
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      safetyProvider.createEnforcement({
        user_id: targetId,
        action,
        reason: reason.trim(),
        report_id: reportId,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      const label = ENFORCEMENT_ACTIONS.find((a) => a.value === action)?.label;
      toast.success(`${label} applied to ${targetName ?? "user"}.`);
      queryClient.invalidateQueries({ queryKey: ["enforcements"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.userEnforcements(targetId) });
      if (reportId) markReviewed.mutate();
      reset();
      onOpenChange(false);
    },
    onError: (error: Error) =>
      toast.error(error.message || "Could not apply the enforcement."),
  });

  function handleSubmit() {
    const trimmed = reason.trim();
    if (!targetId) {
      toast.error("Pick a user first.");
      return;
    }
    if (trimmed.length === 0) {
      toast.error("A reason is required — the user receives it verbatim by email.");
      return;
    }
    if (trimmed.length > 2000) {
      toast.error("Reason must be 2000 characters or fewer.");
      return;
    }
    setConfirming(true);
  }

  function confirmSummary() {
    const name = targetName ?? "this user";
    if (action === "ban_user") {
      return `Ban ${name} — ${expiresAt ? `until ${new Date(expiresAt).toLocaleString()}` : "permanently"}?`;
    }
    if (action === "strike_user") return `Strike ${name}?`;
    return `Warn ${name}?`;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {targetName ? `Enforce against ${targetName}` : "New enforcement"}
          </DialogTitle>
          <DialogDescription>
            Account-level and reversible — nothing the user created is deleted.
            Reported content is still handled per item in the report queue.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {confirming && (
            <div className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <p className="font-medium">{confirmSummary()}</p>
              <p className="text-xs text-muted-foreground">
                This will immediately email and push-notify {targetName ?? "the user"}.
                There's no undo on the notification — lifting the enforcement later
                won't recall it.
              </p>
            </div>
          )}
          {!userId && (
            <div className="flex flex-col gap-2">
              <Label>User</Label>
              <UserPicker selected={picked} onSelect={setPicked} />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label>Action</Label>
            <Select
              value={action}
              onValueChange={(v) => {
                const next = v as EnforcementAction;
                setAction(next);
                if (next !== "ban_user") setExpiresAt("");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENFORCEMENT_ACTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ENFORCEMENT_ACTIONS.find((a) => a.value === action)?.hint}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="enforcement-reason">Reason</Label>
            <Textarea
              id="enforcement-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Repeated harassment in event chat"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Sent to the user verbatim. {reason.trim().length}/2000
            </p>
          </div>

          {locks && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="enforcement-expires">Expires</Label>
              <Input
                id="enforcement-expires"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {expiresAt
                  ? "The ban lifts automatically at this time."
                  : "Empty — this ban is permanent."}
              </p>
            </div>
          )}

          {locks && (
            <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
              Open websockets close immediately. A user already holding a live
              session can keep making requests for up to about an hour on an
              otherwise-idle session — the ban is enforced immediately on any
              active use (an API call, a login), and always immediately over an
              open socket. Once it's discovered, every request is refused, not
              just the next one.
            </p>
          )}
          {!locks && (
            <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
              {action === "strike_user" ? "A strike" : "A warning"} is recorded
              and delivered — it does not suspend, log out, or disconnect the
              user.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => (confirming ? setConfirming(false) : onOpenChange(false))}
            disabled={create.isPending}
          >
            {confirming ? "Back" : "Cancel"}
          </Button>
          <Button
            variant={action === "ban_user" ? "destructive" : "default"}
            onClick={confirming ? () => create.mutate() : handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending
              ? "Applying…"
              : confirming
                ? "Confirm"
                : `Apply ${ENFORCEMENT_ACTIONS.find((a) => a.value === action)?.label.toLowerCase()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
